package com.tovtalk.data.cache

import com.tovtalk.data.network.BandwidthEstimator
import com.tovtalk.domain.entity.Post
import io.ktor.client.*
import io.ktor.client.call.body
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.contentLength
import kotlinx.coroutines.*

class VideoPreloadManager(
    private val httpClient: HttpClient,
    private val bandwidth: BandwidthEstimator,
    private val cache: SessionVideoCache = SessionVideoCache()
) {
    private val scope = CoroutineScope(Dispatchers.Default + SupervisorJob())
    private val activeJobs = mutableMapOf<String, Job>()

    fun onVideoVisible(currentIndex: Int, feedItems: List<Post>) {
        cancelOutdatedPreloads(currentIndex, feedItems)

        val preloadTargets = listOf(
            currentIndex to 0.40,
            currentIndex + 1 to 0.20,
            currentIndex + 2 to 0.10
        )

        for ((index, percent) in preloadTargets) {
            if (index < feedItems.size) {
                val post = feedItems[index]
                val quality = bandwidth.recommendedQuality()
                val url = post.allSources[quality.key] ?: post.videoUrl
                val totalSize = post.videoSizes?.get(quality.key)
                preloadPartial(url, percent, totalSize)
            }
        }
    }

    private fun preloadPartial(url: String, percent: Double, knownSizeBytes: Int? = null) {
        if (activeJobs.containsKey(url)) return

        activeJobs[url] = scope.launch {
            if (cache.contains(url)) return@launch
            try {
                val totalBytes = knownSizeBytes?.toLong() ?: run {
                    val headResponse = httpClient.head(url)
                    headResponse.contentLength() ?: return@launch
                }
                val targetBytes = (totalBytes * percent).toLong()

                val response = httpClient.get(url) {
                    header("Range", "bytes=0-${targetBytes - 1}")
                }
                val bytes = response.body<ByteArray>()
                cache.put(url, bytes)
            } catch (e: CancellationException) {
                throw e
            } catch (_: Exception) {
                // Preload failure is not critical
            } finally {
                activeJobs.remove(url)
            }
        }
    }

    private fun cancelOutdatedPreloads(currentIndex: Int, feedItems: List<Post>) {
        val relevantUrls = (currentIndex..minOf(currentIndex + 2, feedItems.size - 1))
            .flatMap { i -> feedItems[i].allSources.values }
            .toSet()

        activeJobs.entries.removeAll { (url, job) ->
            if (url !in relevantUrls) {
                job.cancel()
                true
            } else false
        }
    }

    suspend fun getCachedData(url: String): ByteArray? = cache.get(url)

    fun clearAll() {
        scope.coroutineContext.cancelChildren()
        activeJobs.clear()
        scope.launch { cache.clear() }
    }
}
